import React from 'react';
import {
  BarChart3,
  Bot,
  Code2,
  Database,
  FileText,
  Lightbulb,
  Receipt,
  Rocket,
  ShieldCheck,
  Sparkles,
  Truck,
  Users,
} from 'lucide-react';
import Navbar from '../../components/website/Navbar';
import Footer from '../../components/website/Footer';

const features = [
  'Merchant Dashboard',
  'Customer Portal',
  'Super Admin Dashboard',
  'Subscription Management',
  'Daily Delivery Generation',
  'Billing & PDF Invoices',
  'Payment Integration (Razorpay Test Mode)',
  'Customer Import (CSV)',
  'Business Reports & Analytics',
  'AI Assistant for Business Operations',
  'AI Mamu Assistant for chat and voice conversations',
  'Local-language voice replies and business information lookup',
];

const stack = [
  { title: 'Frontend', icon: Code2, items: ['React', 'Tailwind CSS'] },
  { title: 'Backend', icon: ShieldCheck, items: ['FastAPI', 'Python'] },
  { title: 'Database & Authentication', icon: Database, items: ['Supabase', 'PostgreSQL', 'Supabase Auth', 'Supabase Storage'] },
  { title: 'Payments', icon: Receipt, items: ['Razorpay (Test Mode)'] },
  { title: 'Documents', icon: FileText, items: ['ReportLab'] },
];

const gptUses = [
  'Refine product requirements and business workflows.',
  'Design the database schema and data relationships.',
  'Improve the subscription, delivery, billing, and payment logic.',
  'Review architecture decisions and identify potential edge cases.',
  'Generate and refine API designs.',
  'Help write project documentation, Devpost content, and technical explanations.',
  'Review implementation decisions and suggest improvements.',
  'Shape the AI Mamu Assistant workflow for chat, voice, and local-language business support.',
];

const codexUses = [
  'Building frontend components.',
  'Developing FastAPI endpoints.',
  'Implementing CRUD operations.',
  'Connecting frontend and backend APIs.',
  'Integrating Supabase authentication and database access.',
  'Creating dashboard pages and reusable UI components.',
  'Adding AI assistant surfaces for chat and voice-driven business operations.',
  'Debugging implementation issues.',
  'Refactoring code into modular services.',
  'Speeding up repetitive engineering tasks.',
];

const roadmap = [
  'Production payment workflows',
  'WhatsApp automation',
  'Delivery staff management',
  'Route optimization',
  'Inventory management',
  'AI business insights',
  'Regional language support',
  'Demand forecasting',
  'Advanced operational analytics',
];

const SectionLabel = ({ icon: Icon, children }) => (
  <div className="inline-flex items-center gap-1.5 rounded-full bg-rozops-blue-light text-rozops-blue text-xs font-semibold px-3 py-1">
    <Icon className="w-3.5 h-3.5" />
    {children}
  </div>
);

const CheckList = ({ items }) => (
  <ul className="space-y-2.5">
    {items.map((item) => (
      <li key={item} className="flex items-start gap-2 text-sm text-rozops-text-muted leading-relaxed">
        <Sparkles className="w-4 h-4 text-rozops-blue flex-shrink-0 mt-0.5" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export default function ProjectInfo() {
  return (
    <div className="bg-rozops-bg min-h-screen">
      <Navbar />

      <main>
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 items-center">
            <div>
              <SectionLabel icon={Rocket}>Project Info</SectionLabel>
              <h1 className="mt-5 font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-rozops-navy tracking-tight">
                RozOps
              </h1>
              <p className="mt-5 text-lg text-rozops-text-muted leading-relaxed">
                RozOps is an AI Operating System for recurring local businesses such as milk delivery, water can suppliers, kirana stores, newspaper delivery, tiffin services, laundry, and other subscription-based businesses. It helps merchants manage subscriptions, daily deliveries, billing, invoices, payments, and customer operations from a single platform.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: Users, value: '3', label: 'Role-based dashboards' },
                { icon: Truck, value: 'Daily', label: 'Delivery operations' },
                { icon: Receipt, value: 'PDF', label: 'Invoices and billing' },
                { icon: Bot, value: 'Mamu', label: 'Chat and voice assistant' },
              ].map(({ icon: Icon, value, label }) => (
                <div key={label} className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5">
                  <div className="w-10 h-10 rounded-xl bg-rozops-blue-light text-rozops-blue grid place-items-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="mt-4 font-heading font-extrabold text-3xl text-rozops-navy">{value}</div>
                  <div className="mt-1 text-sm text-rozops-text-muted">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <SectionLabel icon={BarChart3}>Features</SectionLabel>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((feature) => (
              <div key={feature} className="bg-white rounded-2xl border border-gray-100 shadow-soft px-5 py-4 text-sm font-semibold text-rozops-text">
                {feature}
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white border-y border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <SectionLabel icon={Code2}>Tech Stack</SectionLabel>
            <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-5 gap-4">
              {stack.map(({ title, icon: Icon, items }) => (
                <div key={title} className="rounded-2xl border border-gray-100 bg-rozops-bg p-5">
                  <div className="w-10 h-10 rounded-xl bg-white text-rozops-blue grid place-items-center mb-4">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h2 className="font-heading font-bold text-base text-rozops-navy">{title}</h2>
                  <ul className="mt-3 space-y-1.5 text-sm text-rozops-text-muted">
                    {items.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <SectionLabel icon={Sparkles}>How We Used Codex & GPT-5.6</SectionLabel>
          <div className="mt-8 grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-6">
              <h2 className="font-heading font-bold text-xl text-rozops-navy">GPT-5.6</h2>
              <p className="mt-2 text-sm text-rozops-text-muted leading-relaxed">
                GPT-5.6 was used as an engineering and product copilot to validate ideas, challenge assumptions, and improve the overall product architecture.
              </p>
              <div className="mt-5"><CheckList items={gptUses} /></div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-6">
              <h2 className="font-heading font-bold text-xl text-rozops-navy">Codex</h2>
              <p className="mt-2 text-sm text-rozops-text-muted leading-relaxed">
                Codex reduced development time, allowing more effort to be spent on solving operational business problems instead of boilerplate implementation.
              </p>
              <div className="mt-5"><CheckList items={codexUses} /></div>
            </div>
          </div>
        </section>

        <section className="bg-white border-y border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-10 items-start">
              <div>
                <SectionLabel icon={Bot}>AI Mamu Assistant</SectionLabel>
                <h2 className="mt-4 font-heading font-extrabold text-3xl sm:text-4xl text-rozops-navy tracking-tight">
                  Chat or speak to understand and run the business
                </h2>
                <p className="mt-4 text-sm sm:text-base text-rozops-text-muted leading-relaxed">
                  AI Mamu Assistant is designed as a friendly business helper for local merchants. A merchant can chat or use voice conversation to ask for details, understand daily operations, and take guided actions in the app.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  'Ask in local languages and get spoken replies.',
                  'Check customer dues, delivery status, and tomorrow requirements.',
                  'Use chat or voice to understand reports and operational details.',
                  'Prepare safe operational actions with confirmation before changes.',
                ].map((item) => (
                  <div key={item} className="rounded-2xl border border-gray-100 bg-rozops-bg p-5">
                    <Bot className="w-5 h-5 text-rozops-blue" />
                    <p className="mt-3 text-sm text-rozops-text-muted leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-6">
              <SectionLabel icon={Lightbulb}>Challenges</SectionLabel>
              <p className="mt-5 text-sm text-rozops-text-muted leading-relaxed">
                The primary challenge was designing a flexible operational engine that works across different recurring-delivery businesses while keeping the user experience simple for small business owners.
              </p>
              <p className="mt-4 text-sm text-rozops-text-muted leading-relaxed">
                Maintaining consistency between subscriptions, deliveries, invoices, and payments required careful backend design to ensure reliable business operations.
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-6">
              <SectionLabel icon={Rocket}>Future Roadmap</SectionLabel>
              <div className="mt-5"><CheckList items={roadmap} /></div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <div className="rounded-3xl bg-gradient-to-br from-rozops-navy via-rozops-accent to-rozops-blue px-6 sm:px-10 py-10 text-white">
            <div className="max-w-3xl">
              <SectionLabel icon={Rocket}>Vision</SectionLabel>
              <p className="mt-5 text-xl sm:text-2xl font-heading font-bold leading-snug">
                RozOps aims to become the AI Operating System for everyday recurring businesses, helping millions of local merchants automate operations and run their businesses with the efficiency of enterprise software, without the complexity.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
